import {
  animate,
  keyframes,
  style,
  transition,
  trigger
} from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSliderModule } from '@angular/material/slider';
import { SvgIconComponent } from '@components/svg-icon/svg-icon.component';
import { Raffle, TrustpilotConfig, User } from '@core/models';
import { ProductTiketsForm } from '@core/models/form-interfaces';
import { MediaInfo } from '@core/models/media-info.model';
import { PaymentCard } from '@core/models/payment-card.model';
import { AuthService } from '@core/services/auth/auth.service';
import { GonativeService } from '@core/services/gonative/gonative.service';
import { Form } from '@core/type/form.type';
import { Select } from '@ngxs/store';
import { TemplateModule } from '@project/shared/template/template.module';
import { AuthState } from '@store/auth/auth.state';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TrustpilotComponent } from '@components/trustpilot/trustpilot.component';
import { PluralizePipe } from '@custom-pipes/index';
@Component({
  standalone: true,
  selector: 'app-product-tickets',
  templateUrl: './product-tickets.component.html',
  styleUrls: ['./product-tickets.component.scss'],
  imports: [
    CommonModule,
    SvgIconComponent,
    MatDialogModule,
    MatSliderModule,
    FormsModule,
    ReactiveFormsModule,
    TemplateModule,
    TrustpilotComponent,
    PluralizePipe
  ],
  animations: [
    trigger('inOutAnimation', [
      transition(':enter', [
        style({ height: 0 }),
        animate(
          '.3s',
          keyframes([
            style({ height: 0, offset: 0 }),
            style({ height: '*', offset: 1 })
          ])
        )
      ]),
      transition(':leave', [
        style({ height: '*' }),
        animate(
          '.3s',
          keyframes([
            style({ height: '*', offset: 0 }),
            style({ height: 0, offset: 1 })
          ])
        )
      ])
    ])
  ]
})
export class ProductTicketsComponent implements OnDestroy, OnInit {
  @Input() productPageText: { [key: string]: string };
  @Input() product: Raffle;
  @Input() closedRaffle: boolean;
  @Input() activeEntryOption: Raffle;
  @Input() formGroup: FormGroup<Form<ProductTiketsForm>>;
  @Input() isAddingToCart: boolean;
  @Input() isModalShown: boolean;
  @Input() isTooMuchTicketPerUser: boolean;
  @Input() errorMessage: string;
  @Input() sliderMaxTickets: number;
  @Input() sliderMinTickets: any;
  @Input() entranceAvailable: boolean;
  @Input() sliderTicketQuantity: number;
  @Input() projectCode: '7days' | 'ukcc' | 'default';
  @Input() dialog: MatDialog;
  @Input() addedTickets: number;
  @Input() tokenizedCards: Array<PaymentCard> | null = [];
  @Input() monthlySpend: number;
  @Input() installAppMode: boolean = false;
  @Input() openBrowserMode: boolean = false;
  @Input() trustpilotConfig: TrustpilotConfig;
  @Input() isInstantWin: boolean;

  @Output() emitHandleSubmit = new EventEmitter();
  @Output() emitModalToggleEmitter = new EventEmitter();
  @Output() handleInstantCheckoutEmitter = new EventEmitter();
  @Output() handleQuantityChange = new EventEmitter<{
    target: { value: number };
  }>();
  @Output() fillProductTickerProductPageFunction = new EventEmitter<Function>();

  @Select(AuthState.hasBoughtIT) hasBoughtIT$: Observable<string[]>;
  @Select(AuthState.user) user$: Observable<User>;

  _alive$ = new Subject<void>();
  min: number | null = 1;
  quickBuyConditions: boolean | null = false;
  isMobile = GonativeService.isGonative();

  constructor(private authState: AuthService) {}

  get getProgress(): string {
    return (
      (this.product?.current_entries * 100) / this.product?.max_entries + '%'
    );
  }

  get galleryProductItems(): MediaInfo[] {
    return [this.product.thumbnail, ...this.product.item_photos];
  }

  get oldPrice(): string {
    return this.product.offers[0].original_price_value_frontend;
  }

  get price() {
    return Number(this.product.offers[0].price);
  }

  get currency() {
    return this.product.offers[0].currency;
  }

  ngOnInit(): void {
    this.hasBoughtIT$.pipe(takeUntil(this._alive$)).subscribe(hasBoughtIT => {
      this.quickBuyConditions = hasBoughtIT.length > 0;
    });
    this.fillProductTickerProductPageFunction.emit(this.handleTicketChange);
  }

  ngOnDestroy(): void {
    this._alive$.next();
    this._alive$.complete();
  }

  chooseEntryOption(entryOption: 'online' | 'free' | any): void {
    this.activeEntryOption = entryOption;
  }

  handleTicketChange(event: { target: { value: number } }): void {
    if (
      event.target.value > this.min &&
      event.target.value < this.sliderMaxTickets
    ) {
      this.formGroup.get('tickets').setValue(event.target.value);
      this.sliderTicketQuantity = event.target.value;
      event.target.value = event.target.value;
    } else if (event.target.value >= this.sliderMaxTickets) {
      this.formGroup.get('tickets').setValue(this.sliderMaxTickets);
      this.sliderTicketQuantity = this.sliderMaxTickets;
      event.target.value = this.sliderMaxTickets;
    } else if (event.target.value <= this.min) {
      this.formGroup.get('tickets').setValue(this.min);
      this.sliderTicketQuantity = this.min;
      event.target.value = this.min;
    }
    if (this.isModalShown) {
      this.handleQuantityChange.emit(event);
    }
  }

  handleIncrement(): void {
    const value = this.formGroup.value.tickets;
    this.handleTicketChange({ target: { value: value + 1 } });
  }

  handleDecrement(): void {
    const value = this.formGroup.value.tickets;
    this.handleTicketChange({ target: { value: value - 1 } });
  }

  slideRangeInput({ value }): void {
    this.handleTicketChange({ target: { value } });
  }

  openBrowser(): void {
    let currentUrl = window.location.href;
    currentUrl +=
      (currentUrl.indexOf('?') > -1 ? '&' : '?') + 'openbrowser=true'; // the rule in GoNative by "openbrowser=true" string
    window.location.href = currentUrl;
  }

  handleSubmit() {
    this.emitHandleSubmit.emit();
    if (this.isModalShown) this.emitModalToggle();
  }

  emitModalToggle() {
    this.emitModalToggleEmitter.emit();
  }

  handleInstantCheckout() {
    this.emitModalToggle();
    this.handleInstantCheckoutEmitter.emit();
  }
}
